"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Datasheet = void 0;
const interface_1 = require("../interface");
const core_1 = require("../core");
const utils_1 = require("../message/utils");
const store_1 = require("../store");
const private_1 = require("../utils/private");
const record_1 = require("./record");
const core_2 = require("../../../core");
const field_1 = require("../script/field");
/**
 * Datasheet operation
 *
 * It is recommended to use if you want to operate datasheet, such as obtaining datasheet data, adding records, deleting records, etc.,
 * we recommend using the {@link useDatasheet} hook function.
 *
 * If you need to obtain record data,
 * you can use {@link useRecord} (query single record data) and {@link useRecords} (batch query record data).
 *
 * - {@link addRecord}: Creates a new record with the specified cell values
 *
 * - {@link addRecords}: Creates multiple new records with the specified cell values
 *
 * - {@link setRecord}: Updates cell values for a record
 *
 * - {@link setRecords}: Updates cell values for multiple records
 *
 * - {@link deleteRecord}: Delete the given record
 *
 * - {@link deleteRecords}: Delete the given records
 *
 * - {@link addField}: Creates a new field
 *
 * - {@link deleteField}: Delete the given field
 *
 * - {@link checkPermissionsForAddRecord}: Checks whether the current user has permission to create the specified record
 *
 * - {@link checkPermissionsForAddRecords}: Checks whether the current user has permission to create the specified records
 *
 * - {@link checkPermissionsForSetRecord}: Checks whether the current user has permission to perform the given record update
 *
 * - {@link checkPermissionsForSetRecords}: Checks whether the current user has permission to perform the given record updates
 *
 * - {@link checkPermissionsForDeleteRecord}: Checks whether the current user has permission to delete the specified record
 *
 * - {@link checkPermissionsForDeleteRecords}: Checks whether the current user has permission to delete the specified records
 *
 * - {@link checkPermissionsForAddField}: Checks whether the current user has permission to create the specified field
 *
 * - {@link checkPermissionsForDeleteField}: Checks whether the current user has permission to delete the specified field
 *
 */
class Datasheet {
    /**
     * @inner
     * @hidden
     */
    constructor(datasheetId, wCtx) {
        var _a;
        this.datasheetId = datasheetId;
        this.wCtx = wCtx;
        this.datasheetData = (_a = wCtx.widgetStore) === null || _a === void 0 ? void 0 : _a.getState().datasheetMap[this.datasheetId];
    }
    /**
     * The unique ID of this datasheet.
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.id);
     * // => 'dstxxxxxxx'
     * ```
     */
    get id() {
        var _a, _b;
        return (_b = (_a = this.datasheetData) === null || _a === void 0 ? void 0 : _a.datasheet) === null || _b === void 0 ? void 0 : _b.datasheetId;
    }
    /**
     * The name of the Datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.name);
     * // => 'Name'
     * ```
     */
    get name() {
        var _a, _b;
        return (_b = (_a = this.datasheetData) === null || _a === void 0 ? void 0 : _a.datasheet) === null || _b === void 0 ? void 0 : _b.datasheetName;
    }
    checkRecordsValues(records) {
        const state = this.wCtx.widgetStore.getState();
        const fieldDataMap = core_1.Selectors.getFieldMap(state, this.datasheetId);
        records.forEach(valuesMap => {
            Object.entries(valuesMap).forEach(([fieldId, cellValue]) => {
                const fieldData = fieldDataMap[fieldId];
                if (!fieldData) {
                    throw new Error(`FieldId: ${fieldId} is not exist in datasheet`);
                }
                const coreField = core_1.Field.bindModel(fieldDataMap[fieldId], state);
                if (!coreField.recordEditable(this.datasheetId)) {
                    throw new Error(`FieldId: ${fieldId} is not editable`);
                }
                if (coreField.validateCellValue(cellValue).error) {
                    throw new Error(`CellValue: ${cellValue} is not validate for field ${fieldId}(${fieldData.name})`);
                }
            });
        });
    }
    transformRecordValues(records) {
        const state = this.wCtx.widgetStore.getState();
        const fieldDataMap = core_1.Selectors.getFieldMap(state, this.datasheetId);
        return records.map(valuesMap => {
            const coreFieldMap = {};
            Object.entries(valuesMap).forEach(([fieldId, cellValue]) => {
                const coreField = core_1.Field.bindModel(fieldDataMap[fieldId], state);
                // Compatible with the case where cellValue is undefined
                coreFieldMap[fieldId] = cellValue === undefined ? null : coreField.openWriteValueToCellValue(cellValue);
            });
            return coreFieldMap;
        });
    }
    checkRecordIdsExist(recordIds) {
        const state = this.wCtx.widgetStore.getState();
        const datasheetId = this.datasheetId;
        for (const recordId of recordIds) {
            // If the recordId is not passed in, the existence verification will not be performed
            if (!recordId) {
                return { acceptable: true };
            }
            const record = core_1.Selectors.getRecord(state, recordId, datasheetId);
            if (!record) {
                return { acceptable: false, message: `record:${recordId} doesn't exist` };
            }
        }
        return { acceptable: true };
    }
    checkBasicPermissions(operation) {
        var _a;
        const state = this.wCtx.widgetStore.getState();
        const datasheetId = this.datasheetId;
        const datasheet = (0, store_1.getWidgetDatasheet)(state, datasheetId);
        const sourceId = (_a = state.widget) === null || _a === void 0 ? void 0 : _a.snapshot.sourceId;
        const globalState = this.wCtx.widgetStore.getState();
        const permissions = core_1.Selectors.getPermissions(globalState, datasheetId, undefined, (sourceId === null || sourceId === void 0 ? void 0 : sourceId.startsWith('mir')) ? sourceId : '');
        if (!datasheet || !permissions) {
            return { acceptable: false, message: 'Failed to load the data of the datasheet' };
        }
        switch (operation) {
            case interface_1.DatasheetOperationPermission.AddRecord:
                {
                    if (!permissions.rowCreatable) {
                        return { acceptable: false, message: 'The permission of the datasheet is read-only and cannot be added record' };
                    }
                }
                break;
            case interface_1.DatasheetOperationPermission.EditRecord:
                {
                    if (!permissions.cellEditable) {
                        return { acceptable: false, message: 'The permission of the datasheet is read-only and cannot be updated cell' };
                    }
                }
                break;
            case interface_1.DatasheetOperationPermission.DeleteRecord:
                {
                    if (!permissions.rowRemovable) {
                        return { acceptable: false, message: 'The permission of the datasheet is read-only and cannot be deleted record' };
                    }
                }
                break;
            case interface_1.DatasheetOperationPermission.AddField:
                {
                    if (!permissions.fieldCreatable) {
                        return { acceptable: false, message: 'The permission of the datasheet is read-only and cannot be added field' };
                    }
                }
                break;
            case interface_1.DatasheetOperationPermission.DeleteField:
                {
                    if (!permissions.fieldRemovable) {
                        return { acceptable: false, message: 'The permission of the datasheet is read-only and cannot be deleted field' };
                    }
                }
                break;
        }
        return { acceptable: true };
    }
    checkPermissionsForRecordsValues(records) {
        const state = this.wCtx.widgetStore.getState();
        const datasheetId = this.datasheetId;
        for (const valuesMap of records) {
            // No value verification will be performed if valuesMap is not transferred
            if (!valuesMap) {
                continue;
            }
            for (const [fieldId, value] of Object.entries(valuesMap)) {
                const fieldPermissionMap = core_1.Selectors.getFieldPermissionMap(state, datasheetId);
                const fieldRole = core_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, fieldId);
                const field = core_1.Selectors.getField(state, fieldId, datasheetId);
                if (!field) {
                    return (0, private_1.errMsg)(`The fieldId of the current operation: ${fieldId} does not exist`);
                }
                if (fieldRole === core_1.ConfigConstant.Role.None || fieldRole === core_1.ConfigConstant.Role.Reader) {
                    return (0, private_1.errMsg)(`No write permission for ${field.name}(${fieldId}) column`);
                }
                const fieldEntity = core_1.Field.bindContext(field, state);
                if (fieldEntity.isComputed) {
                    return (0, private_1.errMsg)(`The content of the ${field.name}(${fieldId}) field is automatically generated by calculation and cannot be written`);
                }
                // Compatible with undefined
                const checkError = fieldEntity.validateOpenWriteValue(!value ? null : value).error;
                if (checkError) {
                    return (0, private_1.errMsg)(`
            The currently written value ${value} of the ${field.name} field does not conform to the format of ${checkError.message}. Please check.`);
                }
            }
        }
        return {
            acceptable: true,
        };
    }
    checkFieldName(name) {
        if (typeof name !== 'string') {
            return (0, private_1.errMsg)('Field name must be a string');
        }
        const state = this.wCtx.widgetStore.getState();
        const fieldDataMap = core_1.Selectors.getFieldMap(state, this.datasheetId);
        const hasTheSameName = Object.keys(fieldDataMap).some(fieldId => {
            const item = fieldDataMap[fieldId];
            return item.name === name;
        });
        if (hasTheSameName) {
            return (0, private_1.errMsg)(`${name} is already exist, Please enter a unique field name`);
        }
        return { acceptable: true };
    }
    checkPrimaryField(fieldId) {
        const state = this.wCtx.widgetStore.getState();
        const snapshot = core_1.Selectors.getSnapshot(state, this.datasheetId);
        return Boolean((snapshot === null || snapshot === void 0 ? void 0 : snapshot.meta.views[0].columns[0].fieldId) === fieldId);
    }
    /**
     * All fields of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.fields);
     * // => [Field, Field]
     * ```
     */
    getFields(viewId) {
        var _a;
        const state = this.wCtx.widgetStore.getState();
        const currentView = this.getViewByIdOrNameIfExists(viewId);
        const fieldDataMap = core_1.Selectors.getFieldMap(state, this.datasheetId);
        const finalView = currentView || core_1.Selectors.getViewsList(state, this.datasheetId)[0];
        return (_a = (finalView.columns || [])) === null || _a === void 0 ? void 0 : _a.map(({ fieldId }) => {
            return new field_1.Field(this.datasheetId, this.wCtx, fieldDataMap[fieldId]);
        });
    }
    getViewByIdOrNameIfExists(viewKey) {
        const state = this.wCtx.widgetStore.getState();
        const views = core_1.Selectors.getViewsList(state, this.datasheetId);
        const finalView = views.find(({ id, name }) => [id, name].includes(viewKey));
        if (!finalView) {
            return null;
        }
        return finalView;
    }
    /**
     * Get the record by record ID in the datasheet.
     *
     * @param recordId the ID of the record.
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.getRecordAsync(myRecordId));
     * // => Record
     * ```
     */
    getRecordAsync(recordId) {
        return new record_1.Record(this.datasheetId, this.wCtx, recordId);
    }
    /**
     * Batch get records in the datasheet.
     *
     * @param options.recordIds List of record IDs
     *
     * @param options.sorts Which fields the records need to be sorted by.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.getRecordsAsync()); // => [Record, Record, ...]
     * console.log(myDatasheet.getRecordsAsync({ recordIds: [myRecordId01, myRecordId02] })); // => [Record, Record]
     * ```
     */
    getRecordsAsync(options) {
        var _a;
        const state = this.wCtx.widgetStore.getState();
        const currentView = core_1.Selectors.getCurrentView(state, this.datasheetId);
        const finalView = currentView || core_1.Selectors.getViewsList(state, this.datasheetId)[0];
        const viewDerivate = core_2.ViewDerivateFactory.createViewDerivate(state, this.datasheetId, core_2.ViewType.Grid);
        const viewDerivation = viewDerivate.getViewDerivation(finalView);
        let finalRows;
        if (options == null || !((_a = options.recordIds) === null || _a === void 0 ? void 0 : _a.length)) {
            finalRows = viewDerivation.visibleRows;
        }
        else {
            const { recordIds } = options;
            finalRows = recordIds === null || recordIds === void 0 ? void 0 : recordIds.map(recordId => ({ recordId }));
        }
        const sorts = options === null || options === void 0 ? void 0 : options.sorts;
        if (sorts === null || sorts === void 0 ? void 0 : sorts.length) {
            const snapshot = (0, store_1.getSnapshot)(state, this.datasheetId);
            finalRows = core_1.Selectors.sortRowsBySortInfo(state, finalRows, sorts, snapshot);
        }
        return finalRows.map(({ recordId }) => {
            return new record_1.Record(this.datasheetId, this.wCtx, recordId);
        });
    }
    /**
     * Creates a new record with the specified cell values.
     *
     * @param valuesMap object mapping fieldId to value for that field.
     *
     * @param insertPosition position to insert in view.
     *
     * @returns The returned promise will resolve to the recordId of the new record once it is persisted.
     *
     * #### Description
     * Adds a new record and optionally specifies its position in the view (default at the end), returning an array of new record IDs.
     *
     * An error will be thrown when the user does not have permission to perform the operation or when the cell value format check does not pass.
     *
     * Refer to {@link FieldType} for cell value write formats.
     *
     * #### Example
     * ```js
     * async function addNewRecord(valuesMap) {
     *   if (datasheet.checkPermissionsForAddRecord(valuesMap).acceptable) {
     *     const newRecordId = await datasheet.addRecord(valuesMap);
     *     alert(`The newly created record ID is ${newRecordId}`);
     *
     *     // Next, you can select, or manipulate, the newly created records.
     *     // ...
     *   }
     * }
     *
     * // The key of the parameter is the fieldId and the value is the cell value.
     * addNewRecord({
     *   fld1234567980: 'this is a text value',
     *   fld0987654321: 1024,
     * });
     *
     * // Different types of field cell values have specific data structures that need to be passed in correctly
     * addNewRecord({
     *   fld1234567890: 'this is a text value', // SingleLineText
     *   fld0987654321: 1024, // Number
     *   fld1234567891: 'option 1', // SingleSelect
     *   fld1234567892: ['option 1', 'option 2'], // MultiSelect
     *   fld1234567893:  1635513510962, // DateTime
     *   fld1234567894: ['rec1234567'], // MagicLink (recordId)
     * });
     * ```
     */
    addRecord(valuesMap = {}, insertPosition) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.addRecords([{ valuesMap }], insertPosition))[0];
        });
    }
    /**
     * Creates new records with the specified cell values.
     *
     * @param records Array of objects with a fields key mapping fieldId to value for that field.
     *
     * @param insertPosition Position to insert in the view.
     *
     * @returns The returned promise will resolve to an array of recordIds of the new records once the new records are persisted.
     *
     * #### Description
     * Add multiple records and optionally specify its position in the view (inserted at the end by default).
     *
     * An error will be thrown when the user does not have permission to perform the operation or when the cell value format check does not pass.
     *
     * Refer to {@link FieldType} for cell value write formats.
     *
     * #### Example
     * ```js
     * const records = [
     *   // Cell values should generally have format matching the output of
     *   {
     *     valuesMap: {
     *       fld1234567890: 'this is a text value',
     *       fld0987654321: 1024,
     *     },
     *   },
     *   // Specifying no fields will create a new record with no cell values set
     *   {
     *     valuesMap: {},
     *   },
     *   // Different types of field cell values have specific data structures that need to be passed in correctly
     *   {
     *     valuesMap: {
     *       fld1234567890: 'Cat video 2', // SingleLineText
     *       fld0987654321: 1024, // Number
     *       fld1234567891: 'option 1', // SingleSelect
     *       fld1234567892: ['option 1', 'option 2'], // MultiSelect
     *       fld1234567893:  1635513510962, // DateTime (Timestamp)
     *       fld1234567894: ['rec1234567'], // MagicLink (recordId)
     *     },
     *   },
     * ];
     *
     * async function addNewRecords() {
     *   if (datasheet.checkPermissionToAddRecords(records)) {
     *     const recordIds = await datasheet.addRecords(records);
     *
     *     alert(`new records with IDs: ${recordIds}`);
     *
     *     // Next, you can select, or manipulate, the newly created records
     *     // ...
     *   }
     * }
     * ```
     */
    addRecords(records = [], insertPosition) {
        const state = this.wCtx.widgetStore.getState();
        const recordsValues = [];
        for (const record of records) {
            recordsValues.push(record.valuesMap);
        }
        const transformedRecords = this.transformRecordValues(recordsValues);
        this.checkRecordsValues(transformedRecords);
        // Default position
        let view = core_1.Selectors.getCurrentView(state, this.datasheetId);
        let viewId = view.id;
        let index = view.rows.length;
        // Designated location
        if (insertPosition) {
            // Iterate through rows to find the actual insertion location, same as common/components/menu datasheet right click UI operation
            // TODO: This approach will need to be optimized in the future
            index = view.rows.findIndex(row => row.recordId === insertPosition.anchorRecordId);
            if (index === -1) {
                throw new Error(`Anchor recordId: ${insertPosition.anchorRecordId} is not exist in datasheet`);
            }
            viewId = insertPosition.viewId;
            const snapshot = core_1.Selectors.getSnapshot(state, this.datasheetId);
            view = core_1.Selectors.getViewById(snapshot, viewId);
        }
        if (index < 0) {
            throw new Error('Insert index should not less than 0');
        }
        if (index > view.rows.length) {
            throw new Error('Insert index should not greater than all row count in view');
        }
        return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.AddRecords,
                datasheetId: this.datasheetId,
                viewId,
                count: transformedRecords.length,
                index,
                cellValues: transformedRecords,
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
            if (result.result === core_1.ExecuteResult.None) {
                throw new Error('Add record method has been ignored');
            }
            resolve(result.data);
        }));
    }
    /**
     * Updates cell values for a record.
     *
     * @param recordId the record to update.
     *
     * @param valuesMap key for fieldId, value for the contents of the cell object,
     * only need to pass to modify the value, do not need to modify the key value do not need to pass.
     * To empty a field, you need to pass key: null.
     * @return
     *
     * #### Description
     * Throws an error if the user does not have permission to update the given cell values in the record, or or recordId does not exist,
     * or when the written value type does not match.
     *
     * We refer to a field in a record as a cell.
     * Refer to {@link FieldType} for cell value write formats.
     *
     *
     * If you need to modify multiple records at the same time, use the {@link setRecords}.
     *
     * #### Example
     * ```js
     * function setRecord(recordId, valuesMap) {
     *   if (datasheet.checkPermissionsForSetRecord(recordId, valuesMap).acceptable) {
     *     datasheet.setRecord(recordId, valuesMap);
     *   }
     * }
     * ```
     */
    setRecord(recordId, valuesMap = {}) {
        return this.setRecords([{ id: recordId, valuesMap }]);
    }
    /**
     * Updates cell values for records.
     * @param records Specify the records be modified.
     * @return
     *
     * #### Description
     * Throws an error if the user does not have permission to update the given cell values in the record, or or recordId does not exist,
     * or when the written value type does not match.
     *
     * valuesMap key for fieldId, value for the contents of the cell object,
     * only need to pass to modify the value, do not need to modify the key value do not need to pass.
     * To empty a field, you need to pass key: null.
     *
     * We refer to a field in a record as a cell.
     * Refer to {@link FieldType} for cell value write formats.
     *
     *
     * If you only need to modify a single record, use the {@link setRecord}
     *
     * #### Example
     * ```js
     * function setRecord(id, valuesMap) {
     *   if (datasheet.checkPermissionsForSetRecords([{ id, valuesMap }]).acceptable) {
     *     datasheet.setRecords([{ id, valuesMap }]);
     *   }
     * }
     * ```
     */
    setRecords(records) {
        return __awaiter(this, void 0, void 0, function* () {
            const recordIds = [];
            const recordsValues = [];
            for (const record of records) {
                recordIds.push(record.id);
                recordsValues.push(record.valuesMap);
            }
            const state = this.wCtx.widgetStore.getState();
            const transformedRecordsValues = this.transformRecordValues(recordsValues);
            this.checkRecordsValues(transformedRecordsValues);
            const data = records.reduce((pre, record) => {
                const recordId = record.id;
                const recordData = core_1.Selectors.getRecord(state, recordId, this.datasheetId);
                if (!recordData) {
                    throw new Error(`RecordId: ${recordId} is not exist in datasheet`);
                }
                this.transformRecordValues([record.valuesMap]).forEach(valueMap => {
                    Object.entries(valueMap).forEach(([fieldId, cellValue]) => {
                        pre.push({
                            recordId,
                            fieldId,
                            value: cellValue,
                        });
                    });
                });
                return pre;
            }, []);
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.SetRecords,
                datasheetId: this.datasheetId,
                data,
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
        });
    }
    /**
     * Delete the given record.
     *
     * @param recordId the record to be deleted.
     * @returns
     *
     * #### Description
     * Delete a record by recordId.
     *
     * Throws an error if the user does not have permission to delete the given record.
     *
     * #### Example
     * ```js
     * async function deleteRecord(recordId) {
     *   if (datasheet.checkPermissionsForDeleteRecord(recordId).acceptable) {
     *     await datasheet.deleteRecord(recordId);
     *     alert('The record has been deleted');
     *
     *     // Record deletion has been saved to servers
     *   }
     * }
     * ```
     */
    deleteRecord(recordId) {
        return this.deleteRecords([recordId]);
    }
    /**
     * Delete the given records.
     *
     * @param recordIds array of recordIds.
     * @returns
     *
     *
     * #### Description
     * Delete the given record by recordIds.
     *
     * Throws an error if the user does not have permission to delete the given record.
     *
     * #### Example
     * ```js
     * async function deleteRecords(recordIds) {
     *   if (datasheet.checkPermissionsForDeleteRecords(recordIds).acceptable) {
     *     await datasheet.deleteRecords(recordIds);
     *     alert('The records has been deleted');
     *
     *     // Records deletion has been saved to servers
     *   }
     * }
     * ```
     */
    deleteRecords(recordIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.DeleteRecords,
                datasheetId: this.datasheetId,
                data: recordIds,
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
        });
    }
    /**
     * Creates a new field.
     *
     * @param name name for the field. must be case-insensitive unique
     * @param type type for the field.
     * @param property property for the field. omit for fields without writable property.
     * @returns
     *
     *
     * #### Description
     *
     * Refer to {@link FieldType} for supported field types, the write format for property, and other specifics for certain field types.
     *
     * Throws an error if the user does not have permission to create a field,
     * if invalid name, type or property are provided, or if creating fields of this type is not supported.
     *
     * #### Example
     * ```js
     * function addField(name, type, property) {
     *   if (datasheet.checkPermissionsForAddField(name, type, property).acceptable) {
     *     datasheet.addField(recordIds);
     *   }
     * }
     * ```
     */
    addField(name, type, property) {
        return __awaiter(this, void 0, void 0, function* () {
            const state = this.wCtx.widgetStore.getState();
            const view = core_1.Selectors.getCurrentView(state, this.datasheetId);
            const index = view.columns.length;
            const fieldType = (0, core_1.getFieldTypeByString)(type);
            const fieldInfoForState = {
                id: (0, core_1.getNewId)(core_1.IDPrefix.Field),
                name,
                type: fieldType,
                property: (0, core_1.getFieldClass)(fieldType).defaultProperty(),
            };
            const field = core_1.Field.bindContext(fieldInfoForState, state);
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.AddFields,
                data: [{
                        data: {
                            name,
                            type: fieldType,
                            property: field.addOpenFieldPropertyTransformProperty(property),
                        },
                        index,
                    }],
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
            if (result.result === core_1.ExecuteResult.None) {
                throw new Error('Add field method has been ignored');
            }
            return result.data;
        });
    }
    /**
     * Delete the given field.
     *
     * @param fieldId the field to be deleted.
     * @param conversion
     * When deleting a field as an associated field,
     * mark whether the associated field of the associated datasheet is deleted or converted to text,
     * the default is Converted to a text field.
     * @returns
     *
     *
     * #### Description
     *
     * Throws an error if the user does not have permission to delete a field.
     *
     * #### Example
     * ```js
     * function deleteField(fieldId) {
     *   if (datasheet.checkPermissionsForDeleteField(fieldId).acceptable) {
     *     datasheet.deleteField(fieldId);
     *   }
     * }
     * ```
     */
    deleteField(fieldId, conversion) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check the primary column. It is allowed to delete the primary column. After deletion, the datasheet will crash.
            if (this.checkPrimaryField(fieldId)) {
                throw new Error(`${fieldId} is Primary field, cannot be deleted`);
            }
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.DeleteField,
                data: [{
                        deleteBrotherField: conversion === core_1.Conversion.Delete,
                        fieldId,
                    }],
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
            if (result.result === core_1.ExecuteResult.None) {
                throw new Error('Delete field method has been ignored');
            }
            return result.data;
        });
    }
    /**
     * Checks whether the current user has permission to create the specified record.
     *
     * @param valuesMap object mapping fieldId to value for that field.
     * @returns
     *
     *
     * #### Description
     * Accepts partial input, in the same format as {@link addRecord}. The more information provided, the more accurate the permissions check will be.
     *
     * The format of valuesMap is the same as when writing to cells. For cell value writing format, refer to {@link FieldType}.
     *
     * Returns `{acceptable: true}` if the current user can create the specified record.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Check if user can create a specific record, when you already know what
     * // fields/cell values will be set for the record.
     * const setRecordCheckResult = datasheet.checkPermissionsForAddRecord({
     *   'fld1234567890': 'Advertising campaign',
     *   'fld0987654321': 1024,
     * });
     * if (!setRecordCheckResult.acceptable) {
     *   alert(setRecordCheckResult.message);
     * }
     *
     * // Check if user could potentially create a record.
     * // Use when you don't know the specific fields/cell values yet (for example,
     * // to show or hide UI controls that let you start creating a record.)
     * const addUnknownRecordCheckResult =
     *   datasheet.checkPermissionsForAddRecord();
     * ```
     */
    checkPermissionsForAddRecord(valuesMap) {
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.AddRecord);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        return this.checkPermissionsForRecordsValues(valuesMap ? [valuesMap] : []);
    }
    /**
     * Checks whether the current user has permission to create the specified records.
     *
     * @param records Array of objects mapping fieldId to value for that field.
     * @returns
     *
     *
     * #### Description
     * array of objects mapping fieldId to value for that field.
     *
     * Accepts partial input, in the same format as {@link addRecords}.
     * The more information provided, the more accurate the permissions check will be.
     *
     * The format of records is the same as when writing to cells. For cell value writing format, refer to {@link FieldType}.
     *
     * Returns `{acceptable: true}` if the current user can update the specified record.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Check if user can update a specific records, when you already know what
     * // fields/cell values will be set for the record.
     * const addRecordsCheckResult = datasheet.checkPermissionsForAddRecords([
     *   {
     *     valuesMap: {
     *       fld1234567890: 'this is a text value',
     *       fld0987654321: 1024,
     *     },
     *   },
     *   {
     *     valuesMap: {
     *       fld1234567890: 'this is another text value',
     *       fld0987654321: 256,
     *     },
     *   },
     *   {},
     * ]);
     * if (!addRecordsCheckResult.acceptable) {
     *   alert(addRecordsCheckResult.message);
     * }
     * // Check if user could potentially create a record.
     * // Use when you don't know the specific fields/cell values yet (for example,
     * // to show or hide UI controls that let you start creating a record.)
     * // same as checkPermissionsForSetRecord
     * const addUnknownRecordCheckResult =
     *   datasheet.checkPermissionsForAddRecords();
     * ```
     */
    checkPermissionsForAddRecords(records) {
        const recordsValues = [];
        for (const record of (records || [])) {
            recordsValues.push(record.valuesMap);
        }
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.AddRecord);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        return this.checkPermissionsForRecordsValues(recordsValues || []);
    }
    /**
     * Checks whether the current user has permission to perform the given record update.
     *
     * @param recordId the record to update
     *
     * @param valuesMap specified as object mapping fieldId to value for that field
     *
     * @returns {@link IPermissionResult}
     *
     * #### Description
     *
     * This method performs **permission** and **value legality** checks based on the level of detail of the value passed in.
     * Passing in valuesMap will check the legality of cell writes and column permissions,
     * and passing in recordId will check the existence of records and modification permissions.
     *
     * The format of records is the same as when writing to cells. For cell value writing format, refer to {@link FieldType}.
     *
     * Returns `{acceptable: true}` if the current user can update the specified record.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Check if user can update specific fields for a specific record
     * const setRecordCheckResult =
     *   datasheet.checkPermissionsForSetRecord('rec1234567', {
     *     'fld1234567890': 'this is a text value',
     *     'fld0987654321': 1024,
     *   });
     * if (!setRecordCheckResult.acceptable) {
     *   alert(setRecordCheckResult.message);
     * }
     *
     * // Checks if a user has permission to modify a record, but does not check if the specific value can be modified
     * const setUnknownFieldsCheckResult =
     *   datasheet.checkPermissionsForSetRecord('rec1234567');
     *
     * // Check whether the user has permission to modify the corresponding field, do not care about the specific record
     * const setUnknownRecordCheckResult =
     *   datasheet.checkPermissionsForSetRecord(undefined, {
     *     'fld1234567890': 'this is a text value',
     * 		// You can also choose not to pass in a specific value and use undefined instead, which will not perform a value type check
     *     'fld0987654321': undefined,
     *   });
     * // Check if user could perform updates within the datasheet, without knowing the
     * // specific record or fields that will be updated yet (e.g., to render your
     * // extension in "read only" mode)
     * const setUnknownRecordAndFieldsCheckResult =
     *   datasheet.checkPermissionsForSetRecord();
     * ```
     */
    checkPermissionsForSetRecord(recordId, valuesMap) {
        return this.checkPermissionsForSetRecords([{ id: recordId, valuesMap }]);
    }
    /**
     * Checks whether the current user has permission to perform the given record updates.
     *
     * @param records Array of objects containing recordId and fields/cellValues to update for that records.
     *
     * @returns {@link IPermissionResult}
     *
     * #### Description
     *
     * This method performs **permission** and **value legality** checks based on the level of detail of the value passed in.
     * Passing in valuesMap will check the legality of cell writes and column permissions,
     * and passing in recordId will check the existence of records and modification permissions.
     *
     * The format of records is the same as when writing to cells. For cell value writing format, refer to {@link FieldType}.
     *
     * Returns `{acceptable: true}` if the current user can update the specified records.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * const recordsToSet = [
     *   {
     *     // Validating a complete record update
     *     id: record1.id,
     *     valuesMap: {
     *       // fields can be specified by ID
     *       fld1234567890: 'this is a text value',
     *       fld0987654321: 1024,
     *     },
     *   },
     *   {
     *     id: record2.id,
     *     valuesMap: {
     *       // If only a fieldId is passed, only the cell of this record will be modified, and the other cells will not be modified
     *       fld1234567890: 'another text value',
     *     },
     *   },
     *   {
     *     // Validating an update to a specific record, not knowing what fields will be updated
     *     id: record3.id,
     *   },
     *   {
     *     // Validating an update to specific cell values, not knowing what record will be updated
     *     valuesMap: {
     *       fld1234567890: 'another text value',
     *       // You can use undefined if you know you're going to update a field, but don't know the new cell value yet.
     *       fld0987654321: undefined,
     *     },
     *   },
     * ];
     *
     * const checkResult = datasheet.checkPermissionsForSetRecords(recordsToSet);
     * if (!checkResult.acceptable) {
     *   console.log(checkResult.message);
     * }
     *
     * // Check if user could potentially update records.
     * // Equivalent to datasheet.checkPermissionsForSetRecord()
     * const setUnknownRecordAndFieldsCheckResult =
     *   datasheet.checkPermissionsForSetRecords();
     * ```
     *
     */
    checkPermissionsForSetRecords(records) {
        const recordIds = [];
        const recordsValues = [];
        for (const record of records) {
            recordIds.push(record.id);
            recordsValues.push(record.valuesMap);
        }
        // records checks existence
        const recordIdsExist = this.checkRecordIdsExist(recordIds);
        if (!recordIdsExist.acceptable) {
            return recordIdsExist;
        }
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.EditRecord);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        return this.checkPermissionsForRecordsValues(recordsValues);
    }
    /**
     * Checks whether the current user has permission to delete the specified record.
     *
     * @param recordId the record to be deleted.
     * @returns
     *
     *
     * #### Description
     * Accepts optional input.
     *
     * Returns `{acceptable: true}` if the current user can delete the specified record.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Check if user can delete a specific record
     * const deleteRecordCheckResult =
     *   datasheet.checkPermissionsForDeleteRecord(recordId);
     * if (!deleteRecordCheckResult.acceptable) {
     *   alert(deleteRecordCheckResult.message);
     * }
     *
     * // Check if user could potentially delete a record.
     * // Use when you don't know the specific record you want to delete yet (for
     * // example, to show/hide UI controls that let you select a record to delete).
     * const deleteUnknownRecordCheckResult =
     *   datasheet.checkPermissionsForDeleteRecord();
     * ```
     */
    checkPermissionsForDeleteRecord(recordId) {
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.DeleteRecord);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        if (!recordId) {
            return basicPermissionsCheckResult;
        }
        return this.checkPermissionsForDeleteRecords([recordId]);
    }
    /**
     * Checks whether the current user has permission to delete the specified records.
     *
     * @param recordIds the records to be deleted.
     * @returns
     *
     *
     * #### Description
     * Accepts optional input.
     *
     * Returns `{acceptable: true}` if the current user can delete the specified records.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Check if user can delete specific records
     * const deleteRecordsCheckResult =
     *   datasheet.checkPermissionsForDeleteRecords([recordId1. recordId2]);
     * if (!deleteRecordsCheckResult.acceptable) {
     *   alert(deleteRecordsCheckResult.message);
     * }
     *
     * // Check if user could potentially delete records.
     * // Use when you don't know the specific records you want to delete yet (for
     * // example, to show/hide UI controls that let you select records to delete).
     * // Equivalent to datasheet.checkPermissionsForDeleteRecord
     * const deleteUnknownRecordsCheckResult =
     *   datasheet.checkPermissionsForDeleteRecords();
     * ```
     */
    checkPermissionsForDeleteRecords(recordIds) {
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.DeleteRecord);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        if (!recordIds) {
            return basicPermissionsCheckResult;
        }
        return this.checkRecordIdsExist(recordIds);
    }
    /**
     * Checks whether the current user has permission to create a field.
     *
     * @param name name for the field. must be case-insensitive unique.
     * @param type type for the field.
     * @param property property for the field. omit for fields without writable property.
     * @returns
     *
     *
     * #### Description
     *
     * Accepts partial input, in the same format as {@link addField}.
     *
     * Returns `{acceptable: true}` if the current user can create the specified field.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Checks whether the current user has permission to create a field
     * const addFieldCheckResult =
     *   datasheet.checkPermissionsForAddField(recordId);
     * if (!addFieldCheckResult.acceptable) {
     *   alert(addFieldCheckResult.message);
     * }
     *
     * // Check if user could potentially create a field.
     * // Use when you don't know the specific a field you want to create yet (for example,
     * // to show or hide UI controls that let you start creating a field.)
     * const addUnknownFieldCheckResult =
     *   datasheet.checkPermissionsForAddField();
     * ```
     */
    checkPermissionsForAddField(name, type, property) {
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.AddField);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        const checkFieldName = this.checkFieldName(name);
        if (!checkFieldName.acceptable) {
            return checkFieldName;
        }
        let fieldType = undefined;
        if (type) {
            fieldType = (0, core_1.getFieldTypeByString)(type);
            if (!fieldType) {
                return (0, private_1.errMsg)('Unknown field type');
            }
        }
        if (fieldType && property != null) {
            const fieldInfoForState = {
                id: (0, core_1.getNewId)(core_1.IDPrefix.Field),
                // New fields are added and no longer populated with a field name by default
                name,
                type: fieldType,
                property: (0, core_1.getFieldClass)(fieldType).defaultProperty(),
            };
            const state = this.wCtx.widgetStore.getState();
            const field = core_1.Field.bindContext(fieldInfoForState, state);
            const { error } = field.validateAddOpenFieldProperty(property || null);
            if (error) {
                return (0, private_1.errMsg)(`current property ${JSON.stringify(property)} does not match the format, please check: ${error.message}`);
            }
        }
        return { acceptable: true };
    }
    /**
     * Checks whether the current user has permission to delete a field.
     *
     * @param fieldId the field to be deleted
     * @returns
     *
     *
     * #### Description
     *
     * Returns `{acceptable: true}` if the current user can delete the specified field.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Checks whether the current user has permission to delete a field.
     * const deleteFieldCheckResult =
     *   datasheet.checkPermissionsForDeleteField(fieldId);
     * if (!deleteFieldCheckResult.acceptable) {
     *   alert(deleteFieldCheckResult.message);
     * }
     *
     * // Check if user could potentially delete a field.
     * // Use when you don't know the specific a field you want to delete yet (for
     * // example, to show/hide UI controls that let you select a field to delete).
     * const deleteUnknownFieldCheckResult =
     *   datasheet.checkPermissionsForDeleteField();
     * ```
     */
    checkPermissionsForDeleteField(fieldId) {
        const basicPermissionsCheckResult = this.checkBasicPermissions(interface_1.DatasheetOperationPermission.DeleteField);
        if (!basicPermissionsCheckResult.acceptable) {
            return basicPermissionsCheckResult;
        }
        if (!fieldId) {
            return basicPermissionsCheckResult;
        }
        const state = this.wCtx.widgetStore.getState();
        const fieldPermissionMap = core_1.Selectors.getFieldPermissionMap(state, this.datasheetId);
        const fieldRole = core_1.Selectors.getFieldRoleByFieldId(fieldPermissionMap, fieldId);
        const field = core_1.Selectors.getField(state, fieldId, this.datasheetId);
        if (!field) {
            return (0, private_1.errMsg)(`Current deleted fieldId: ${fieldId} does not exist`);
        }
        if (this.checkPrimaryField(fieldId)) {
            return (0, private_1.errMsg)(`${field.name}(${fieldId}) is Primary field, cannot be deleted`);
        }
        if (fieldRole === core_1.ConfigConstant.Role.None || fieldRole === core_1.ConfigConstant.Role.Reader) {
            return (0, private_1.errMsg)(`No write access for ${field.name}(${fieldId}) column`);
        }
        return { acceptable: true };
    }
}
exports.Datasheet = Datasheet;
